import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Globe,
  ToggleLeft,
  ToggleRight,
  Star,
} from 'lucide-react'
import { api } from '../services/api'
import Button from '../components/Button'
import Input from '../components/Input'

export default function Languages() {
  const [searchTerm, setSearchTerm] = useState('')
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingLanguage, setEditingLanguage] = useState(null)
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    native_name: '',
    flag: '',
    is_active: true,
    is_default: false,
    sort_order: 0,
  })

  const queryClient = useQueryClient()

  const {
    data: languagesData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['languages'],
    queryFn: () => api.get('/languages').then((res) => res.data),
  })

  const languages = Array.isArray(languagesData?.data)
    ? languagesData.data
    : Array.isArray(languagesData)
    ? languagesData
    : []
  const meta = languagesData?.meta || {}

  const filteredLanguages = languages.filter(
    (lang) =>
      lang.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lang.native_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lang.code.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const createLanguageMutation = useMutation({
    mutationFn: (data) => api.post('/languages', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['languages'])
      setShowAddForm(false)
      resetForm()
    },
  })

  const updateLanguageMutation = useMutation({
    mutationFn: ({ id, data }) => api.put(`/languages/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['languages'])
      setEditingLanguage(null)
      resetForm()
    },
  })

  const deleteLanguageMutation = useMutation({
    mutationFn: (id) => api.delete(`/languages/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['languages'])
    },
  })

  const toggleActiveMutation = useMutation({
    mutationFn: (id) => api.patch(`/languages/${id}/toggle-active`),
    onSuccess: () => {
      queryClient.invalidateQueries(['languages'])
    },
  })

  const setDefaultMutation = useMutation({
    mutationFn: (id) => api.patch(`/languages/${id}/set-default`),
    onSuccess: (data, id) => {
      queryClient.invalidateQueries(['languages'])
      if (editingLanguage) {
        setFormData((prev) => ({
          ...prev,
          is_default: editingLanguage.id === id,
        }))
      }
    },
  })

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      native_name: '',
      flag: '',
      is_active: true,
      is_default: false,
      sort_order: 0,
    })
  }

  const handleEdit = (language) => {
    setEditingLanguage(language)
    setFormData({
      code: language.code,
      name: language.name,
      native_name: language.native_name,
      flag: language.flag || '',
      is_active: language.is_active,
      is_default: language.is_default,
      sort_order: language.sort_order,
    })
    setShowAddForm(true)
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (editingLanguage) {
      updateLanguageMutation.mutate({
        id: editingLanguage.id,
        data: formData,
      })
    } else {
      createLanguageMutation.mutate(formData)
    }
  }

  const handleDelete = (language) => {
    if (window.confirm(`Are you sure you want to delete ${language.name}?`)) {
      deleteLanguageMutation.mutate(language.id)
    }
  }

  const handleToggleActive = (language) => {
    toggleActiveMutation.mutate(language.id)
  }

  const handleSetDefault = (language) => {
    setDefaultMutation.mutate(language.id)
  }

  useEffect(() => {
    if (editingLanguage && languages.length > 0) {
      const currentLanguage = languages.find(
        (lang) => lang.id === editingLanguage.id
      )
      if (currentLanguage) {
        setFormData((prev) => ({
          ...prev,
          is_default: currentLanguage.is_default,
        }))
      }
    }
  }, [languages, editingLanguage])

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Languages</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage available languages for your content.
          </p>
        </div>
        <Button onClick={() => setShowAddForm(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Language
        </Button>
      </div>

      {/* Search */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search languages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Language Form */}
      {showAddForm && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {editingLanguage ? 'Edit Language' : 'Add New Language'}
          </h3>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Language Name
                </label>
                <Input
                  placeholder="e.g., English"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Native Name
                </label>
                <Input
                  placeholder="e.g., English"
                  value={formData.native_name}
                  onChange={(e) =>
                    setFormData({ ...formData, native_name: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Language Code
                </label>
                <Input
                  placeholder="e.g., en"
                  value={formData.code}
                  onChange={(e) =>
                    setFormData({ ...formData, code: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Flag Emoji
                </label>
                <Input
                  placeholder="e.g., 🇺🇸"
                  value={formData.flag}
                  onChange={(e) =>
                    setFormData({ ...formData, flag: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Sort Order
                </label>
                <Input
                  type="number"
                  placeholder="0"
                  value={formData.sort_order}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      sort_order: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData({ ...formData, is_active: e.target.checked })
                    }
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Active
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_default}
                    onChange={(e) =>
                      setFormData({ ...formData, is_default: e.target.checked })
                    }
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Default Language
                  </span>
                </label>
              </div>
            </div>
            <div className="mt-4 flex justify-end space-x-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setShowAddForm(false)
                  setEditingLanguage(null)
                  resetForm()
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  createLanguageMutation.isPending ||
                  updateLanguageMutation.isPending
                }
              >
                {editingLanguage ? 'Update Language' : 'Add Language'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Languages Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Available Languages
          </h3>

          {isLoading ? (
            <div className="text-center py-8">
              <div className="text-gray-500">Loading languages...</div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="text-red-500">
                Error loading languages: {error.message}
              </div>
            </div>
          ) : filteredLanguages.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-500">
                {searchTerm
                  ? 'No languages found matching your search.'
                  : 'No languages configured yet.'}
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Language
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Default
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredLanguages.map((language) => (
                    <tr key={language.code}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-2xl mr-3">{language.flag}</span>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {language.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {language.native_name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 font-mono">
                          {language.code}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            language.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {language.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {language.is_default && (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            Default
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleEdit(language)}
                            className="text-gray-400 hover:text-gray-600"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleToggleActive(language)}
                            className={`${
                              language.is_active
                                ? 'text-green-400 hover:text-green-600'
                                : 'text-gray-400 hover:text-gray-600'
                            }`}
                            title={
                              language.is_active ? 'Deactivate' : 'Activate'
                            }
                            disabled={language.is_default}
                          >
                            {language.is_active ? (
                              <ToggleRight className="h-4 w-4" />
                            ) : (
                              <ToggleLeft className="h-4 w-4" />
                            )}
                          </button>
                          {!language.is_default && (
                            <button
                              onClick={() => handleSetDefault(language)}
                              className="text-blue-400 hover:text-blue-600"
                              title="Set as Default"
                            >
                              <Star className="h-4 w-4" />
                            </button>
                          )}
                          {!language.is_default && (
                            <button
                              onClick={() => handleDelete(language)}
                              className="text-red-400 hover:text-red-600"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Language Statistics */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Globe className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Languages
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {meta.total || languages.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-6 w-6 bg-green-500 rounded-full flex items-center justify-center">
                  <span className="text-xs text-white font-bold">✓</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Active Languages
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {meta.active || languages.filter((l) => l.is_active).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-6 w-6 bg-blue-500 rounded-full flex items-center justify-center">
                  <span className="text-xs text-white font-bold">★</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Default Language
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {meta.default_locale ||
                      languages.find((l) => l.is_default)?.name ||
                      'None'}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
